/**
 * Stage 1 API shapes (docs/BUILD_PLAN.md → Stage 1 contract). The worker
 * returns D1 rows, so booleans may arrive as 0/1 and JSON columns as strings;
 * the helpers at the bottom normalise both.
 */
import type { ScreeningStatus, ScreeningSummary } from '@/features/screening/types';

export type StageKind = 'open' | 'won' | 'lost';

export interface Stage {
  id: string;
  name: string;
  kind: StageKind;
  sort_order: number;
  color: string | null;
  is_entry: boolean | 0 | 1;
  system_key: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Role {
  id: string;
  slug: string;
  title: string;
  sort_order?: number;
  is_active?: boolean | 0 | 1;
}

export type Qualification = 'qualified' | 'needs_review' | 'not_qualified' | null;

/** Stage 2 — resume columns on the lead, summarised for the UI. */
export type ResumeParseStatus = 'none' | 'pending' | 'parsed' | 'failed' | 'unsupported';

export interface ResumeSummary {
  filename: string | null;
  content_type: string | null;
  size: number | null;
  uploaded_at: string | null;
  parse_status: ResumeParseStatus;
  parse_error?: string | null;
  parsed_at: string | null;
}

/** `ai_fields_json.resume` — what the parser extracted (BUILD_PLAN Stage 2). */
export interface EmploymentEntry {
  employer: string;
  title: string;
  start: string | null;
  end: string | null;
  is_current: boolean;
  location?: string | null;
  highlights: string[];
}

export interface ResumeExtraction {
  summary: string | null;
  employment_history: EmploymentEntry[];
  skills: string[];
  certifications: string[];
  years_experience: number | null;
}

/** Stage 3 — personality assessment invite (`assessment_invites`), summarised for the UI. */
export type AssessmentStatus = 'not_sent' | 'sent' | 'opened' | 'completed';

export interface AssessmentSummary {
  status: AssessmentStatus;
  sent_at: string | null;
  opened_at: string | null;
  completed_at: string | null;
}

/** Roles that receive the work-style assessment (BUILD_PLAN Stage 3). */
export const ASSESSMENT_ROLE_SLUGS: ReadonlySet<string> = new Set(['service-advisor', 'general-manager']);

export function roleTakesAssessment(role: Pick<Role, 'slug'> | null | undefined): boolean {
  return !!role && ASSESSMENT_ROLE_SLUGS.has(role.slug);
}

/** Stage 3 — one row of the outbound/inbound `messages` queue (`GET /api/leads/:id/messages`). */
export type MessageDirection = 'outbound' | 'inbound';
export type MessageChannel = 'sms' | 'slack';
export type MessageStatus = 'queued' | 'sent' | 'failed' | 'would_send' | 'received';

export interface Message {
  id: string;
  lead_id?: string | null;
  direction: MessageDirection;
  channel: MessageChannel;
  to_address: string | null;
  from_address: string | null;
  body: string;
  kind: string | null;
  status: MessageStatus;
  error?: string | null;
  scheduled_for?: string | null;
  sent_at: string | null;
  created_at: string;
}

/** Stage 5 — a feedback nudge waiting on (or answered by) a recipient's text reply. */
export type PromptKind = 'status_question' | 'notes_question';

/** `lead.nudge` on `GET /api/leads/:id` — `null` when the lead was never nudged. */
export interface NudgeSummary {
  last_sent_at: string | null;
  /** The prompt still waiting for a reply, or `null` when none is open. */
  open_prompt_kind: PromptKind | null;
}

/** One row of `pending_prompts` (`GET /api/leads/:id/prompts`). */
export interface Prompt {
  id: string;
  kind: PromptKind;
  phone_e164: string | null;
  recipient_name?: string | null;
  sent_at: string | null;
  expires_at: string | null;
  answered_at: string | null;
  /** The raw reply (`1`–`4` for a status question, free text or `NO` for notes). */
  answer: string | null;
  created_at: string;
}

/** One card on the board (`GET /api/leads`). */
export interface LeadCard {
  id: string;
  candidate_id?: string;
  first_name: string;
  last_name: string;
  phone_e164?: string | null;
  email: string | null;
  role_id: string;
  role_title: string;
  alias_title: string | null;
  shop_id: string | null;
  stage_id: string;
  stage_entered_at: string;
  source: string | null;
  qualification: Qualification;
  has_resume: boolean | 0 | 1;
  /** Stage 3: `null`/absent when no invite was ever sent. */
  assessment_status?: AssessmentStatus | null;
  /** Stage 4: status of the latest screening call, or `null` when none was scheduled. */
  screening_status?: ScreeningStatus | null;
  /** Stage 5: a nudge text is out and nobody has replied yet. */
  nudge_pending?: boolean | 0 | 1;
  last_event_at: string | null;
  note_count: number;
  created_at?: string;
}

export type ActorKind = 'user' | 'system' | 'ai' | 'integration';

export interface LeadEvent {
  id: string;
  type: string;
  from_stage_id: string | null;
  to_stage_id: string | null;
  actor_user_id: string | null;
  actor_kind: ActorKind;
  payload_json: string | Record<string, unknown> | null;
  created_at: string;
}

export interface LeadNote {
  id: string;
  body: string;
  author_user_id: string | null;
  author_kind: string;
  author_name?: string | null;
  created_at: string;
}

export interface Candidate {
  id: string;
  first_name: string;
  last_name: string;
  phone_e164: string;
  email: string | null;
}

/** `GET /api/leads/:id` — the lead row plus its candidate, role, events and notes. */
export interface LeadDetail {
  id: string;
  candidate_id: string;
  role_id: string;
  alias_title: string | null;
  shop_id: string | null;
  stage_id: string;
  source: string | null;
  campaign: string | null;
  utm_json: string | Record<string, unknown> | null;
  answers_json: string | Record<string, unknown> | null;
  ai_fields_json?: string | Record<string, unknown> | null;
  qualification: Qualification;
  qualification_reason: string | null;
  assigned_user_id: string | null;
  stage_entered_at: string;
  created_at: string;
  updated_at: string;
  candidate: Candidate;
  role: Role;
  /** `null` until a file is uploaded. */
  resume?: ResumeSummary | null;
  /** Stage 3: `null` until an invite is sent. */
  assessment?: AssessmentSummary | null;
  /** Stage 4: `null` until a screening call is scheduled. */
  screening?: ScreeningSummary | null;
  /** Stage 5: `null` until the first nudge goes out. */
  nudge?: NudgeSummary | null;
  events: LeadEvent[];
  notes: LeadNote[];
}

/** The resume columns as they sit on the lead row (`0003_resume.sql`). */
export interface ResumeColumns {
  has_resume?: boolean | 0 | 1;
  resume_uploaded_at?: string | null;
  resume_filename?: string | null;
  resume_content_type?: string | null;
  resume_size?: number | null;
  resume_parse_status?: ResumeParseStatus | null;
  resume_parse_error?: string | null;
  resume_parsed_at?: string | null;
}

/**
 * `GET /api/leads/:id` (and the `PATCH` response) exactly as the worker sends
 * it — api/README.md → "Pipeline API → Leads": the lead row is NESTED under
 * `lead`, JSON columns arrive parsed as `utm` / `answers` / `ai_fields`, and
 * each event carries `payload` (an object), not `payload_json`.
 */
export interface WorkerLeadDetail {
  lead: {
    id: string;
    company_id?: string;
    candidate_id: string;
    role_id: string;
    alias_title: string | null;
    shop_id: string | null;
    stage_id: string;
    source: string | null;
    campaign: string | null;
    utm?: Record<string, unknown> | null;
    answers?: Record<string, unknown> | null;
    ai_fields?: Record<string, unknown> | null;
    qualification: Qualification;
    qualification_reason: string | null;
    assigned_user_id: string | null;
    stage_entered_at: string;
    created_at: string;
    updated_at: string;
  } & ResumeColumns;
  candidate: Candidate & { name?: string };
  role: Role;
  stage?: { id: string; name: string; kind: StageKind; color: string | null; system_key: string | null } | null;
  /** Stage 2: the resume summary. Older workers only send the columns on `lead`. */
  resume?: ResumeSummary | null;
  /** Stage 3: the latest assessment invite, or `null`. */
  assessment?: AssessmentSummary | null;
  /** Stage 4: the screening summary, or `null`. */
  screening?: ScreeningSummary | null;
  /** Stage 5: the nudge summary, or `null`. */
  nudge?: NudgeSummary | null;
  events: WorkerLeadEvent[];
  notes: LeadNote[];
}

/** Builds the `ResumeSummary` from the lead's columns when the worker sends no `resume` block. */
export function resumeFromColumns(lead: ResumeColumns): ResumeSummary | null {
  if (!truthy(lead.has_resume) && !lead.resume_filename && !lead.resume_uploaded_at) return null;
  return {
    filename: lead.resume_filename ?? null,
    content_type: lead.resume_content_type ?? null,
    size: lead.resume_size ?? null,
    uploaded_at: lead.resume_uploaded_at ?? null,
    parse_status: lead.resume_parse_status ?? 'none',
    parse_error: lead.resume_parse_error ?? null,
    parsed_at: lead.resume_parsed_at ?? null,
  };
}

export type WorkerLeadEvent = Omit<LeadEvent, 'payload_json'> & {
  payload?: Record<string, unknown> | null;
  payload_json?: LeadEvent['payload_json'];
};

/**
 * Flattens the worker shape into the `LeadDetail` the UI renders. A value that
 * is already flat (older fixtures) passes through untouched. QA stage 1: the
 * drawer used to read `lead.id` / `lead.stage_id` off the nested envelope and
 * got `undefined`, which broke "Move to stage", shop assignment and notes.
 */
export function normalizeLeadDetail(raw: WorkerLeadDetail | LeadDetail): LeadDetail {
  const events: LeadEvent[] = ((raw.events ?? []) as WorkerLeadEvent[]).map((e) => {
    const { payload, payload_json, ...rest } = e;
    return { ...rest, payload_json: payload_json ?? payload ?? null };
  });
  if ('lead' in raw && raw.lead && typeof raw.lead === 'object') {
    const { utm, answers, ai_fields, ...lead } = raw.lead;
    return {
      ...lead,
      utm_json: utm ?? null,
      answers_json: answers ?? null,
      ai_fields_json: ai_fields ?? null,
      candidate: raw.candidate,
      role: raw.role,
      resume: raw.resume === undefined ? resumeFromColumns(raw.lead) : raw.resume,
      assessment: raw.assessment ?? null,
      screening: raw.screening ?? null,
      nudge: raw.nudge ?? null,
      events,
      notes: raw.notes ?? [],
    };
  }
  return { ...(raw as LeadDetail), events };
}

function isEntry(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function strings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string' && s.trim() !== '') : [];
}

/** `ai_fields.resume`, defensively typed — the parser output is LLM JSON. */
export function resumeExtraction(lead: Pick<LeadDetail, 'ai_fields_json'>): ResumeExtraction | null {
  const resume = jsonObject(lead.ai_fields_json).resume;
  if (!resume || typeof resume !== 'object' || Array.isArray(resume)) return null;
  const r = resume as Record<string, unknown>;
  const history = (Array.isArray(r.employment_history) ? r.employment_history : []).filter(isEntry).map((x) => {
    return {
      employer: typeof x.employer === 'string' ? x.employer : '',
      title: typeof x.title === 'string' ? x.title : '',
      start: typeof x.start === 'string' ? x.start : null,
      end: typeof x.end === 'string' ? x.end : null,
      is_current: x.is_current === true,
      location: typeof x.location === 'string' ? x.location : null,
      highlights: strings(x.highlights),
    };
  });
  return {
    summary: typeof r.summary === 'string' && r.summary.trim() ? r.summary : null,
    employment_history: history,
    skills: strings(r.skills),
    certifications: strings(r.certifications),
    years_experience: typeof r.years_experience === 'number' ? r.years_experience : null,
  };
}

export interface LeadFilters {
  q?: string;
  role_id?: string;
  shop_id?: string;
  stage_id?: string;
}

export interface LeadPatch {
  stage_id?: string;
  shop_id?: string | null;
  assigned_user_id?: string | null;
  role_id?: string;
}

export interface StageCreate {
  name: string;
  kind: StageKind;
  color?: string;
  after_stage_id?: string;
}

export interface StagePatch {
  name?: string;
  color?: string;
  kind?: StageKind;
}

export interface StageDeleteResult {
  warning?: 'system_stage_removed';
  system_key?: string | null;
}

export function truthy(v: boolean | 0 | 1 | null | undefined): boolean {
  return v === true || v === 1;
}

export function jsonObject(v: string | Record<string, unknown> | null | undefined): Record<string, unknown> {
  if (!v) return {};
  if (typeof v === 'object') return v;
  try {
    const parsed: unknown = JSON.parse(v);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

export function fullName(p: { first_name?: string | null; last_name?: string | null }): string {
  const name = `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  return name || 'Unnamed candidate';
}
